import { jsx as _jsx } from "react/jsx-runtime";
/**
 * Shared ObservabilityProvider
 *
 * Platform-agnostic provider for analytics, feature flags, and error monitoring.
 * Each app passes in platform-specific backends (e.g. posthog-js vs
 * posthog-react-native). The provider handles user identification via useOrby()
 * and syncs the analytics session with error monitoring.
 */
import { createContext, useContext, useEffect, useState, useCallback, useMemo, useRef } from "react";
import { notEmpty } from "@orb-labs/types";
import { useOrby } from "../components/OrbyProvider/OrbyProvider.js";
import { setBackends, capture as moduleCapture, captureException } from "../observability/capture.js";
import { DEFAULT_FEATURE_FLAGS } from "../observability/defaultFeatureFlags.js";
const ObservabilityContext = createContext(null);
export const ObservabilityProvider = ({ analytics, errorMonitoring, children, }) => {
    const { accountCluster, loading: orbyLoading } = useOrby();
    const [flags, setFlags] = useState(() => analytics.getAllFlags?.() ?? {});
    const [isLoading, setIsLoading] = useState(true);
    const previousUserIdRef = useRef(undefined);
    const syncFlagsFromBackend = useCallback(async () => {
        await analytics.reloadFlags?.();
        setFlags(analytics.getAllFlags?.() ?? {});
        if (errorMonitoring?.setSessionLink) {
            const session = analytics.getSessionInfo?.();
            if (session) {
                errorMonitoring.setSessionLink(session.sessionId, session.distinctId);
            }
        }
    }, [analytics, errorMonitoring]);
    // Load flags on mount so logged-out users get login feature flags before an
    // Orby account cluster exists (guest mode only fetches platforms).
    useEffect(() => {
        setBackends(analytics, errorMonitoring);
        let cancelled = false;
        const loadFlags = async () => {
            try {
                await syncFlagsFromBackend();
            }
            catch (error) {
                captureException(error, { context: "ObservabilityProvider.loadFlags" });
            }
            finally {
                if (!cancelled) {
                    setIsLoading(false);
                }
            }
        };
        loadFlags();
        return () => {
            cancelled = true;
            analytics.shutdown?.();
        };
    }, [analytics, errorMonitoring, syncFlagsFromBackend]);
    // Identify / reset after Orby auth settles — do not block initial flag load.
    useEffect(() => {
        if (orbyLoading) {
            return;
        }
        const syncSession = async () => {
            const userId = accountCluster?.user?.id;
            const previousUserId = previousUserIdRef.current;
            const loggedOut = notEmpty(previousUserId) && !notEmpty(userId);
            if (notEmpty(userId) && notEmpty(accountCluster)) {
                const props = {
                    account_count: accountCluster.accounts?.length ?? 0,
                    account_cluster_id: accountCluster.accountClusterId,
                };
                analytics.identify(userId, props);
                errorMonitoring?.identifyUser(userId, props);
                errorMonitoring?.setTag?.("orby_user_id", userId);
                errorMonitoring?.setTag?.("account_cluster_id", accountCluster.accountClusterId);
            }
            else if (loggedOut) {
                analytics.reset();
                errorMonitoring?.clearUser();
            }
            previousUserIdRef.current = userId;
            try {
                await syncFlagsFromBackend();
            }
            catch (error) {
                captureException(error, { context: "ObservabilityProvider.syncSession" });
            }
        };
        syncSession();
    }, [analytics, errorMonitoring, syncFlagsFromBackend, orbyLoading, accountCluster]);
    const isEnabled = useCallback((key) => {
        if (isLoading) {
            if (key in DEFAULT_FEATURE_FLAGS) {
                return DEFAULT_FEATURE_FLAGS[key];
            }
            return false;
        }
        if (Object.prototype.hasOwnProperty.call(flags, key)) {
            return flags[key];
        }
        if (key in DEFAULT_FEATURE_FLAGS) {
            return DEFAULT_FEATURE_FLAGS[key];
        }
        return analytics.isFeatureEnabled?.(key) ?? false;
    }, [flags, isLoading, analytics]);
    const refresh = useCallback(async () => {
        setIsLoading(true);
        try {
            await syncFlagsFromBackend();
        }
        finally {
            setIsLoading(false);
        }
    }, [syncFlagsFromBackend]);
    const capture = useCallback((event, properties) => {
        moduleCapture(event, properties);
    }, []);
    const reset = useCallback(() => {
        analytics.reset();
        errorMonitoring?.clearUser();
    }, [analytics, errorMonitoring]);
    const contextValue = useMemo(() => ({ flags, isLoading, isEnabled, refresh, capture, reset }), [flags, isLoading, isEnabled, refresh, capture, reset]);
    return _jsx(ObservabilityContext.Provider, { value: contextValue, children: children });
};
export const useObservability = () => {
    const context = useContext(ObservabilityContext);
    if (!context) {
        throw new Error("useObservability must be used within ObservabilityProvider");
    }
    return context;
};
export const useFeatureFlag = (key) => {
    const { isEnabled } = useObservability();
    return isEnabled(key);
};
export const useAnalytics = () => {
    const { capture } = useObservability();
    return { capture };
};
